import { useCallback, useRef, useState, type DragEvent } from 'react';

import { getChatDragThreadId, isChatDrag } from '@/utils/chat-drag';

import { useProjectActions } from './project-actions';

export function useChatProjectDropTarget(projectId: string) {
  const { setProjectAssignment } = useProjectActions();
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const resetDragOver = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragOver(false);
  }, []);

  const onDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isChatDrag(event.dataTransfer)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isChatDrag(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isChatDrag(event.dataTransfer)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!isChatDrag(event.dataTransfer)) return;
      event.preventDefault();
      event.stopPropagation();
      resetDragOver();
      const threadId = getChatDragThreadId(event.dataTransfer);
      if (!threadId) return;
      void setProjectAssignment({ kind: 'thread', id: threadId }, projectId);
    },
    [projectId, resetDragOver, setProjectAssignment]
  );

  return {
    isDragOver,
    dropTargetProps: {
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
    },
  };
}
